import { logger } from '../../utils/logger';
import { formatPhoneForSAP, resolveSapCtiContactId } from './phone.util';
import { navigateSapContactViaUiEvent } from './sap-ui-navigation.util';
import { postToSapHost } from '../sap-context/cti-origin.util';
import { CallDirection } from '../../types';
import { escapeXml } from './cti-xml.util';

/**
 * SAP CTI ACCEPT EventType follows call direction.
 * Official / community CTI widgets use OUTBOUND for outbound ACCEPT and
 * INBOUND for inbound ACCEPT; END always uses UPDATEACTIVITY.
 * @see https://help.sap.com/doc/930235431e6148048c0da9c111390a37/CSS_SHIP/en-US/3ptyCT_IIntegEN_1.pdf
 */
export function resolveSapAcceptEventType(
  direction?: CallDirection | null
): 'INBOUND' | 'OUTBOUND' {
  return direction === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND';
}

export function postSapContactNavigation(
  contactId: string,
  displayId: string | undefined,
  referenceId: string,
  phone: string | undefined,
  intent: 'browse' | 'link',
  direction?: CallDirection | null,
  firstName?: string
): void {
  // CustomerID is retained as V2 Business Partner CAD for agent context.
  // Custom_1/Custom_2 drive configured backup identification; ContactID is
  // retained for classic adapters / our END correlation.
  const ctiContactId = resolveSapCtiContactId(contactId, displayId);
  const formattedPhone = phone ? formatPhoneForSAP(phone) : '';
  const eventType = resolveSapAcceptEventType(direction);
  const custom1 = displayId?.trim();
  const custom2 = firstName?.trim();
  const customerIdentificationXml = `${custom1 ? `\n  <Custom_1>${escapeXml(custom1)}</Custom_1>` : ''}${
    custom2 ? `\n  <Custom_2>${escapeXml(custom2)}</Custom_2>` : ''
  }`;

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<payload>
  <Type>CALL</Type>
  <EventType>${eventType}</EventType>
  <Action>ACCEPT</Action>
  <ANI>${formattedPhone}</ANI>
  <CustomerID>${escapeXml(ctiContactId)}</CustomerID>
  <ContactID>${escapeXml(ctiContactId)}</ContactID>${customerIdentificationXml}
  <ExternalReferenceID>${referenceId}</ExternalReferenceID>
</payload>`;

  logger.debug(
    `[SAP CTI] ${intent === 'link' ? 'Linking' : 'Browsing'} contact in SAP:`,
    ctiContactId,
    'eventType:',
    eventType,
    'phone:',
    formattedPhone,
    'refId:',
    referenceId
  );
  postToSapHost(xml);
}

/**
 * Open contact in the SAP Agent Desktop shell without linking to the call.
 * Uses mashup UI navigation only — never CTI ACCEPT (that assigns the call
 * and surfaces phone-call timeline activity) and never a new browser tab.
 */
export function browseSapContact(
  contactId: string,
  displayId: string | undefined,
  _phone?: string,
  contactType: 'individual' | 'corporate' = 'individual'
): void {
  const objectKey = contactId?.trim() || displayId?.trim();
  if (!objectKey) {
    logger.warn('[SAP UI] Cannot browse contact — missing id/displayId');
    return;
  }

  if (window.parent === window) {
    logger.warn('[SAP UI] Cannot browse contact outside SAP iframe — no in-shell host');
    return;
  }

  navigateSapContactViaUiEvent(objectKey, contactType);
}

export function openSapContactViaCTI(
  contactId: string,
  displayId: string | undefined,
  referenceId: string,
  phone?: string,
  contactType: 'individual' | 'corporate' = 'individual',
  direction?: CallDirection | null,
  firstName?: string
): void {
  postSapContactNavigation(contactId, displayId, referenceId, phone, 'link', direction, firstName);
  // Also open the contact in Agent Desktop. ACCEPT links the call (timeline);
  // UI navigation brings the record into view without a new browser tab.
  if (window.parent !== window) {
    navigateSapContactViaUiEvent(contactId, contactType);
  }
}
